import { apiClient } from './client';
import type { CfoFundingEvents, CfoFundingReceipt, CfoFundingLine, CfoFundingLines, CfoFundingHistory, CfoFundingProgress, CfoFundingReview, CfoFundingControl } from './types';
const base = (slug: string) => `/projects/${encodeURIComponent(slug)}/cfo-workflow/funding-imports`;
export async function previewFunding(slug: string, file: File): Promise<CfoFundingReceipt> {
  return (await apiClient.post<CfoFundingReceipt>(`${base(slug)}/preview`, file, {
    headers: { 'Content-Type': 'text/csv', 'X-Filename': file.name.replace(/[^A-Za-z0-9._ -]/g, '_').slice(0, 120) },
  })).data;
}
export async function confirmFunding(slug: string, receipt: CfoFundingReceipt): Promise<CfoFundingReceipt> {
  return (await apiClient.post<CfoFundingReceipt>(`${base(slug)}/${receipt.id}/confirm`, {
    preview_revision: receipt.preview_revision, idempotency_key: `funding-${receipt.id}-${receipt.preview_revision}`,
  })).data;
}
export async function getFunding(slug: string, id: string): Promise<CfoFundingReceipt> {
  return (await apiClient.get<CfoFundingReceipt>(`${base(slug)}/${id}`)).data;
}
/** The import step by step: rounds, collection plans, provider API calls, scoring, cohort. */
export async function getFundingProgress(slug: string, id: string): Promise<CfoFundingProgress> {
  return (await apiClient.get<CfoFundingProgress>(`${base(slug)}/${id}/progress`)).data;
}
export async function getFundingLines(slug: string, id: string, offset = 0): Promise<CfoFundingLines> {
  return (await apiClient.get<CfoFundingLines>(`${base(slug)}/${id}/lines`, { params: { offset, limit: 50 } })).data;
}
export async function recentFunding(slug: string): Promise<CfoFundingHistory> {
  return (await apiClient.get<CfoFundingHistory>(base(slug))).data;
}
export async function controlFunding(slug: string, id: string, request: CfoFundingControl): Promise<CfoFundingReceipt> {
  return (await apiClient.post<CfoFundingReceipt>(`${base(slug)}/${id}/control`, request)).data;
}
export async function reviewFunding(slug: string, id: string, request: CfoFundingReview): Promise<CfoFundingReceipt> {
  return (await apiClient.post<CfoFundingReceipt>(`${base(slug)}/${id}/review`, request)).data;
}

export async function getFundingEvents(slug: string, id: string): Promise<CfoFundingEvents> {
  return (await apiClient.get<CfoFundingEvents>(`${base(slug)}/${id}/history`, { params: { limit: 50 } })).data;
}

/** Every row of an import (at most 1,000, read 100 at a time). */
export async function getAllFundingLines(slug: string, id: string): Promise<CfoFundingLine[]> {
  const page = (offset: number) =>
    apiClient.get<CfoFundingLines>(`${base(slug)}/${id}/lines`, { params: { offset, limit: 100 } });
  const first = (await page(0)).data;
  const offsets = [];
  for (let offset = first.items.length; offset < first.total; offset += 100) offsets.push(offset);
  const rest = await Promise.all(offsets.map(page));
  return [...first.items, ...rest.flatMap((response) => response.data.items)];
}
